// "Two step sign in" (authenticator app) on/off for whoever is signed in:
// owner, staff or admin. Lives in Settings › Security, behind the sidebar
// avatar (so staff, who can't open Settings, can reach it) and in the Admin
// Portal. The Firebase calls are in utils/mfa.ts.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import QRCode from 'qrcode';
import { masterAuth } from '../firebase/master';
import { Button, Modal } from './ui';
import {
  enrolledFactors, refreshEmailVerified, sendVerifyEmail,
  startTotpEnroll, finishTotpEnroll, removeFactor, mfaErrorText,
  isMfaRequired, startSignInMfa, finishSignInMfa,
  type MfaChallenge, type TotpSetup,
} from '../utils/mfa';

type Retry = { action: 'start' } | { action: 'remove'; uid: string };
type Step =
  | { kind: 'idle' }
  | { kind: 'setup'; setup: TotpSetup }                           // scan QR, enter first code
  | { kind: 'reauth'; retry: Retry }                              // password again
  | { kind: 'reauth-code'; retry: Retry; challenge: MfaChallenge }; // password ok, app code for that

const ROW: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const BOX: CSSProperties = { height: 36, boxSizing: 'border-box' };

function StatusPill({ on }: { on: boolean }) {
  const c = on ? 'var(--success)' : 'var(--text-3)';
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
      color: c, background: on ? 'var(--success-bg)' : 'var(--surface-2)',
      border: `1px solid color-mix(in srgb, ${c} 30%, transparent)`,
    }}>
      {on ? 'On' : 'Off'}
    </span>
  );
}

function StepNo({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{
        flexShrink: 0, width: 20, height: 20, borderRadius: 999, fontSize: 11, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--primary-light)', color: 'var(--primary)',
      }}>{n}</span>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, minWidth: 0, flex: 1 }}>{children}</div>
    </div>
  );
}

/** The QR code is drawn here in the browser, so the secret never goes to
 *  an outside QR service. */
function QrImage({ url }: { url: string }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(url, { margin: 1, width: 168, errorCorrectionLevel: 'M' })
      .then(d => { if (alive) setSrc(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [url]);
  return (
    <div style={{ width: 168, height: 168, padding: 6, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, flexShrink: 0 }}>
      {src && <img src={src} alt="QR code for your authenticator app" width={156} height={156} style={{ display: 'block' }} />}
    </div>
  );
}

export function TwoStepPanel() {
  const email = masterAuth.currentUser?.email ?? '';
  const [factors,  setFactors]  = useState(enrolledFactors);
  const [verified, setVerified] = useState(() => masterAuth.currentUser?.emailVerified ?? false);
  const [step,     setStep]     = useState<Step>({ kind: 'idle' });
  const [code,     setCode]     = useState('');
  const [password, setPassword] = useState('');
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState('');
  const [notice,   setNotice]   = useState('');
  const [copied,   setCopied]   = useState(false);
  const [confirmOff, setConfirmOff] = useState<string | null>(null);

  // The cached emailVerified flag goes stale once the user clicks the link.
  useEffect(() => {
    let alive = true;
    refreshEmailVerified().then(v => { if (alive) setVerified(v); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  function fail(err: unknown, retry?: Retry) {
    if ((err as { code?: string })?.code === 'auth/requires-recent-login' && retry) {
      setPassword(''); setCode('');
      setStep({ kind: 'reauth', retry });
      setError('');
      setNotice('For safety, enter your password again.');
      return;
    }
    setError(mfaErrorText(err));
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true); setError(''); setNotice('');
    try { await fn(); } finally { setBusy(false); }
  }

  const startSetup = () => run(async () => {
    try {
      const setup = await startTotpEnroll();
      setCode(''); setCopied(false);
      setStep({ kind: 'setup', setup });
    } catch (e) { fail(e, { action: 'start' }); }
  });

  const remove = (uid: string) => run(async () => {
    try {
      await removeFactor(uid);
      setFactors(enrolledFactors());
      setStep({ kind: 'idle' });
      setNotice('Two step sign in is off.');
    } catch (e) { fail(e, { action: 'remove', uid }); }
  });

  function retry(r: Retry) {
    if (r.action === 'start') startSetup(); else remove(r.uid);
  }

  const onConfirmCode = () => run(async () => {
    if (step.kind !== 'setup') return;
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6 digit code from the app.'); return; }
    try {
      await finishTotpEnroll(step.setup.secret, code);
      setFactors(enrolledFactors());
      setStep({ kind: 'idle' }); setCode('');
      setNotice('Two step sign in is on. Next time you sign in, enter the code from your app.');
    } catch (e) { setCode(''); fail(e); }
  });

  const onReauth = () => run(async () => {
    if (step.kind !== 'reauth') return;
    const u = masterAuth.currentUser;
    if (!u || !u.email) return;
    try {
      await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, password));
      setStep({ kind: 'idle' });
      retry(step.retry);
    } catch (e) {
      if (isMfaRequired(e)) {
        try {
          setCode('');
          setStep({ kind: 'reauth-code', retry: step.retry, challenge: startSignInMfa(e) });
        } catch (e2) { setError(mfaErrorText(e2)); }
      } else {
        const c = (e as { code?: string })?.code;
        setError(c === 'auth/invalid-credential' || c === 'auth/wrong-password' ? 'Wrong password.' : mfaErrorText(e));
      }
    }
  });

  const onReauthCode = () => run(async () => {
    if (step.kind !== 'reauth-code') return;
    try {
      await finishSignInMfa(step.challenge, code);
      setStep({ kind: 'idle' });
      retry(step.retry);
    } catch (e) { setCode(''); setError(mfaErrorText(e)); }
  });

  const onSendVerify = () => run(async () => {
    try { await sendVerifyEmail(); setNotice(`We sent a link to ${email}. Open it, then tap "I have verified".`); }
    catch (e) { setError(mfaErrorText(e)); }
  });

  const onCheckVerified = () => run(async () => {
    const v = await refreshEmailVerified().catch(() => false);
    setVerified(v);
    if (!v) setError('Not verified yet. Open the link in the email first.');
  });

  async function copyKey(key: string) {
    try { await navigator.clipboard.writeText(key); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* the key is visible to copy by hand */ }
  }

  const on = factors.length > 0;
  const codeInput = (onEnter: () => void) => (
    <input
      type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
      placeholder="6 digit code" value={code} autoFocus disabled={busy}
      onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
      onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) onEnter(); }}
      style={{ ...BOX, width: 150, fontFamily: 'var(--font-num)', letterSpacing: code ? '0.25em' : undefined }}
    />
  );

  let body: ReactNode;
  if (step.kind === 'reauth') {
    body = (
      <div className="form-group" style={{ margin: 0 }}>
        <label>Password</label>
        <div style={ROW}>
          <input type="password" autoComplete="current-password" value={password} autoFocus disabled={busy}
            onChange={e => setPassword(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') onReauth(); }}
            style={{ ...BOX, width: 240 }} />
          <Button variant="primary" style={BOX} loading={busy} disabled={!password} onClick={onReauth}>Continue</Button>
          <Button variant="secondary" style={BOX} disabled={busy} onClick={() => { setStep({ kind: 'idle' }); setNotice(''); }}>Cancel</Button>
        </div>
      </div>
    );
  } else if (step.kind === 'reauth-code') {
    body = (
      <div className="form-group" style={{ margin: 0 }}>
        <label>Code from your authenticator app</label>
        <div style={ROW}>
          {codeInput(onReauthCode)}
          <Button variant="primary" style={BOX} loading={busy} disabled={code.length !== 6} onClick={onReauthCode}>Continue</Button>
          <Button variant="secondary" style={BOX} disabled={busy} onClick={() => setStep({ kind: 'idle' })}>Cancel</Button>
        </div>
      </div>
    );
  } else if (step.kind === 'setup') {
    const key = step.setup.secretKey;
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <StepNo n={1}>
          Install <strong>Google Authenticator</strong> or <strong>Microsoft Authenticator</strong> on your phone (free).
        </StepNo>
        <StepNo n={2}>
          In the app, tap <strong>+</strong> and scan this QR code.
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <QrImage url={step.setup.qrUrl} />
            <div style={{ minWidth: 0, flex: '1 1 180px' }}>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 4 }}>Can't scan? Type this key in the app:</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <code style={{ fontSize: 12, fontWeight: 700, padding: '5px 8px', borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--border)', wordBreak: 'break-all', letterSpacing: '0.04em' }}>
                  {key.match(/.{1,4}/g)?.join(' ') ?? key}
                </code>
                <button type="button" className="line-item-link-btn" style={{ fontSize: 12 }} onClick={() => copyKey(key)}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </StepNo>
        <StepNo n={3}>
          Enter the 6 digit code the app shows.
          <div style={{ ...ROW, marginTop: 8 }}>
            {codeInput(onConfirmCode)}
            <Button variant="primary" style={BOX} loading={busy} disabled={code.length !== 6} onClick={onConfirmCode}>Turn on</Button>
            <Button variant="secondary" style={BOX} disabled={busy} onClick={() => setStep({ kind: 'idle' })}>Cancel</Button>
          </div>
        </StepNo>
      </div>
    );
  } else if (on) {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {factors.map(f => (
          <div key={f.uid} style={{ ...ROW, justifyContent: 'space-between', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{f.displayName || 'Authenticator app'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Added {new Date(f.enrollmentTime).toLocaleDateString()}</div>
            </div>
            <button type="button" disabled={busy} onClick={() => setConfirmOff(f.uid)}
              style={{
                background: 'var(--danger-bg)', color: 'var(--danger)', fontWeight: 600, fontSize: 12,
                border: '1.5px solid color-mix(in srgb, var(--danger) 35%, transparent)',
                borderRadius: 'var(--r-sm)', padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit',
              }}>
              Turn off
            </button>
          </div>
        ))}
        <p className="form-hint" style={{ margin: 0 }}>New phone? Turn it off here on your old phone, then set it up again.</p>
      </div>
    );
  } else if (!verified) {
    body = (
      <div>
        <p style={{ fontSize: 13, color: 'var(--text-2)', margin: '0 0 10px' }}>
          First verify your email <strong>{email}</strong>.
        </p>
        <div style={ROW}>
          <Button variant="primary" style={BOX} loading={busy} onClick={onSendVerify}>Send link</Button>
          <Button variant="secondary" style={BOX} disabled={busy} onClick={onCheckVerified}>I have verified</Button>
        </div>
      </div>
    );
  } else {
    body = (
      <div style={ROW}>
        <Button variant="primary" style={BOX} loading={busy} onClick={startSetup}>Set up authenticator app</Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ ...ROW, justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>Two step sign in</div>
        <StatusPill on={on} />
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-3)', margin: '0 0 14px', lineHeight: 1.5 }}>
        After your password, enter a code from an authenticator app on your phone. Someone who knows your password still can't get in.
      </p>

      {body}

      {error  && <div className="msg msg-error" style={{ marginTop: 12 }}>{error}</div>}
      {notice && !error && <div className="msg msg-info" style={{ marginTop: 12 }}>{notice}</div>}

      <Modal
        open={confirmOff !== null}
        onClose={() => setConfirmOff(null)}
        title="Turn off two step sign in?"
        size="sm"
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmOff(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => { const id = confirmOff; setConfirmOff(null); if (id) remove(id); }}>Turn off</Button>
        </>}
      >
        <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
          You will sign in with only your password again.
        </p>
      </Modal>
    </div>
  );
}

/** Same panel in a modal: opened from the sidebar avatar and the Admin Portal. */
export function TwoStepModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title="Sign in security" size="md"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      {open && <TwoStepPanel />}
    </Modal>
  );
}
